import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Bell, BookOpen, FileText, CheckCircle, MessageCircle, RefreshCw, ClipboardList, Trophy, X, Trash2 } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_ICONS = {
  new_content: FileText,
  new_comment: MessageCircle,
  evaluation_score: Trophy,
};

const TYPE_CONTENT_ICONS = {
  lesson: BookOpen,
  task: FileText,
  correction: CheckCircle,
  evaluation: ClipboardList,
  reinforcement: RefreshCw,
  comment: MessageCircle,
};

const TYPE_COLORS = {
  lesson: '#3B82F6',
  task: '#F59E0B',
  correction: '#10B981',
  evaluation: '#EF4444',
  reinforcement: '#8B5CF6',
  comment: '#EC4899',
};

export default function NotificationBell() {
  const { user, token } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const bellRef = useRef(null);
  const panelRef = useRef(null);
  const headers = { headers: { Authorization: `Bearer ${token}` } };

  const fetchUnreadCount = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/notifications/unread-count`, headers);
      setUnreadCount(res.data.count);
    } catch (err) { console.error("[NotificationBell] silent error:", err); }
  }, [token]);

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/notifications`, headers);
      setNotifications(res.data);
    } catch (err) { console.error("[NotificationBell] silent error:", err); }
  }, [token]);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 8000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target) && bellRef.current && !bellRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/mark-read`, {}, headers);
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (err) { console.error("[NotificationBell] silent error:", err); }
  };

  // iter71.9 · Confirmación + borrado masivo. Mantiene un mini-estado
  // local `confirmingClear` para renderizar la ventana de «¿Estás
  // seguro?» dentro del propio panel — no bloqueamos la UI con
  // `window.confirm` porque el panel ya está flotando.
  const [confirmingClear, setConfirmingClear] = useState(false);
  const clearAll = async () => {
    try {
      await axios.delete(`${API}/notifications/clear-all`, headers);
      setNotifications([]);
      setUnreadCount(0);
      setConfirmingClear(false);
    } catch (err) { console.error("[NotificationBell] silent error:", err); }
  };

  const handleNotificationClick = async (notif) => {
    if (!notif.read) {
      await axios.put(`${API}/notifications/mark-read`, { notification_ids: [notif.id] }, headers);
      setUnreadCount(prev => Math.max(0, prev - 1));
      setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
    }
    if (notif.class_id) {
      navigate(`/class/${notif.class_id}`);
      setOpen(false);
    }
  };

  const getTimeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'ahora';
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    return `${days}d`;
  };

  return (
    <div className="relative">
      <button
        ref={bellRef}
        data-testid="notification-bell"
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors text-slate-500 hover:text-slate-700"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-[10px] font-bold text-white px-1"
            style={{ background: '#EF4444', animation: 'pulse 2s infinite' }}
            data-testid="notification-count"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Panel */}
      {open && (
        <div
          ref={panelRef}
          className="absolute right-0 top-12 w-[380px] bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden z-50"
          style={{ animation: 'slideUp 0.25s ease forwards' }}
          data-testid="notification-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3 border-b bg-slate-50">
            <h3 className="text-sm font-bold text-slate-700" style={{ fontFamily: 'Montserrat' }}>
              Notificaciones
            </h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-xs text-blue-500 hover:text-blue-700 font-medium"
                  data-testid="mark-all-read-btn"
                >
                  Marcar todo leido
                </button>
              )}
              {notifications.length > 0 && (
                <button
                  onClick={() => setConfirmingClear(true)}
                  className="text-xs text-red-500 hover:text-red-700 font-medium flex items-center gap-1"
                  data-testid="clear-all-notifications-btn"
                  title="Borrar todas las notificaciones"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Borrar todo
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* iter71.9 · Confirmación inline «¿Estás seguro?» */}
          {confirmingClear && (
            <div
              className="px-5 py-3 bg-amber-50 border-b border-amber-200 flex items-center justify-between gap-3"
              data-testid="clear-all-confirm"
            >
              <p className="text-xs text-amber-800 leading-tight">
                ¿Estás seguro de que deseas borrar todas las notificaciones? Esta acción no se puede deshacer.
              </p>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => setConfirmingClear(false)}
                  className="text-xs px-2.5 py-1 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-100"
                  data-testid="clear-all-cancel-btn"
                >
                  Cancelar
                </button>
                <button
                  onClick={clearAll}
                  className="text-xs px-2.5 py-1 rounded-md bg-red-500 text-white hover:bg-red-600 font-medium"
                  data-testid="clear-all-confirm-btn"
                >
                  Sí, borrar
                </button>
              </div>
            </div>
          )}

          {/* Notifications list */}
          <ScrollArea className="max-h-[400px]">
            {notifications.length === 0 ? (
              <div className="text-center py-10 px-4">
                <Bell className="w-10 h-10 text-slate-200 mx-auto mb-3" />
                <p className="text-sm text-slate-400">No hay notificaciones</p>
              </div>
            ) : (
              <div>
                {notifications.map((notif) => {
                  const ContentIcon = TYPE_CONTENT_ICONS[notif.content_type] || TYPE_ICONS[notif.type] || Bell;
                  const accentColor = TYPE_COLORS[notif.content_type] || '#3B82F6';

                  return (
                    <button
                      key={notif.id}
                      data-testid={`notification-${notif.id}`}
                      onClick={() => handleNotificationClick(notif)}
                      className={`w-full text-left px-5 py-3.5 flex gap-3 transition-colors border-b border-slate-100 last:border-0 ${
                        notif.read ? 'bg-white hover:bg-slate-50' : 'bg-blue-50/50 hover:bg-blue-50'
                      }`}
                    >
                      {/* Icon */}
                      <div
                        className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center"
                        style={{ background: `${accentColor}15` }}
                      >
                        <ContentIcon className="w-5 h-5" style={{ color: accentColor }} />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm leading-snug ${notif.read ? 'text-slate-600' : 'text-slate-800 font-semibold'}`}>
                            {notif.title}
                          </p>
                          <span className="text-[10px] text-slate-400 flex-shrink-0 mt-0.5">
                            {getTimeAgo(notif.created_at)}
                          </span>
                        </div>
                        <p className="text-xs text-slate-400 mt-0.5 truncate">{notif.message}</p>
                      </div>

                      {/* Unread dot */}
                      {!notif.read && (
                        <div className="flex-shrink-0 mt-2">
                          <div className="w-2 h-2 rounded-full bg-blue-500" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </div>
      )}
    </div>
  );
}
